import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { navigate } from '@util/router';
import { useTranslation } from 'react-i18next';
import FlagIcon from '../../images/FlagIcon';
import { useUser } from '@util/providers/AuthProvider';
import { useLocale } from '@util/providers/Locale';
import { sortList } from '@util/functions';
import { useLicense } from '@util/providers/License';
import { useStateWithLocalStorage } from '@util/hooks/useStateWithLocalStorage';
import RegionLoadingModal from './RegionLoadingModal';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from '@ui/components';

let regionsCache = null;
let regionsPromise = null;

function fetchRegions() {
  if (regionsCache) return Promise.resolve(regionsCache);
  if (regionsPromise) return regionsPromise;
  const url =
    process.env.INTERNAL_SERVICE_URL +
    'admin/regions?ShowDisabled=false&ShowUneditable=true';
  regionsPromise = axios(url, { method: 'GET' })
    .then((response) => {
      if (response.status === 200) return response;
      throw Error(response.statusText);
    })
    .then((response) => {
      regionsCache = sortList(response.data);
      return regionsCache;
    })
    .finally(() => {
      regionsPromise = null;
    });
  return regionsPromise;
}

export default function RegionSelect() {
  const { t } = useTranslation();
  const { locale, setLocale, setSelectedCurrency } = useLocale();
  const { licensePlans } = useLicense();
  const { bmoUser } = useUser();
  const [, setHasManualLocaleSelection] = useStateWithLocalStorage(
    'hasManualLocaleSelection',
    false,
    false
  );
  const [dropdownRegions, setDropdownRegions] = useState(regionsCache);
  const [settingDropdownRegions, setSettingDropdownRegions] = useState(
    !regionsCache
  );

  const isSubscriber =
    bmoUser &&
    bmoUser.zuoraInfo &&
    bmoUser.zuoraInfo.subscriptions &&
    bmoUser.zuoraInfo.subscriptions.length !== 0;

  useEffect(() => {
    if (regionsCache) {
      setDropdownRegions(regionsCache);
      setSettingDropdownRegions(false);
      return;
    }
    let cancelled = false;
    fetchRegions()
      .then((data) => {
        if (!cancelled) setDropdownRegions(data);
      })
      .catch((err) => {
        console.error(err.message);
      })
      .finally(() => {
        if (!cancelled) setSettingDropdownRegions(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <UncontrolledDropdown className="block! w-full">
      {dropdownRegions && (
        <>
          <DropdownToggle
            caret
            color="outline-opaque-secondary"
            data-testid="LocaleCountryDropdown"
            className="cursor-pointer select-none rounded border align-middle leading-normal font-normal transition-colors duration-150 ease-in-out focus:outline-none disabled:pointer-events-none disabled:opacity-65 px-5 py-2.5 text-base flex! items-center justify-between w-full text-left"
            disabled={
              isSubscriber ||
              (locale?.localeDetails?.exclusive && locale?.geoLocated === true)
            }
          >
            <span className="flex items-center">
              {locale ? (
                <>
                  <FlagIcon
                    code={locale?.localeDetails?.isoCode?.toLowerCase()}
                    className="mr-2 pr-2"
                  />
                  {locale?.localeDetails?.name}
                </>
              ) : (
                <>
                  <FlagIcon code={'us'} className="mr-2 pr-2" />
                  {t('countries.us')}
                </>
              )}
            </span>
          </DropdownToggle>

          <DropdownMenu style={{ overflowY: 'auto', maxHeight: 'min(40rem, 40vh)' }}>
            {dropdownRegions.map((region) => (
              <DropdownItem
                id={region.isoCode}
                key={region.isoCode}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={
                  region.isoCode === locale?.localeDetails?.isoCode && true
                }
                onClick={() => {
                  setSelectedCurrency(
                    region.isoCode === 'CA'
                      ? 'CAD'
                      : region.isoCode === 'US'
                        ? 'USD'
                        : licensePlans?.pricing[region.isoCode]?.currency
                  );
                  setLocale({
                    localeDetails: region,
                    geoLocated: false,
                  });
                  setHasManualLocaleSelection(true);
                  if (region.exclusive) {
                    navigate('/partner');
                  } else if (window.location.href.includes('partner')) {
                    if (region.defaultToSubscription === true) {
                      navigate('/');
                    } else {
                      navigate('/license');
                    }
                  }
                }}
              >
                <FlagIcon
                  code={region?.isoCode?.toLowerCase()}
                  className="mr-2 pr-2"
                />
                {region.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </>
      )}
      <RegionLoadingModal isOpen={settingDropdownRegions} />
    </UncontrolledDropdown>
  );
}
